import sanitizeHTML from '@pinecast/common/helpers/sanitize';
import {fromMarkdown} from 'mdast-util-from-markdown';
import {toHast} from 'mdast-util-to-hast';
import {toHtml} from 'hast-util-to-html';

import {LANGUAGE_MARK_ATTRIBUTES} from './helpers/languageMarks';
import {escapeTagsInCodeSpans, showNotesDialect} from './markdownDialect';

// The tags that show notes can have: `SAFE_TAGS` in `pinecast/helpers.py`. It is
// not the list of the tags that podcast apps render. It is the list of the tags
// that are safe, and raw HTML that uses one of them passes through. A tag that is
// not here is removed and its text stays (see `sanitize`).
//
// `docs/show-notes-markup.md` in the Django repo says why `DIV` and `SPAN` are
// here. A creator who writes one does it on purpose.
const SHOW_NOTES_TAGS = [
  'A',
  'ABBR',
  'ACRONYM',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CODE',
  'DIV',
  'EM',
  'H1',
  'H2',
  'H3',
  'HR',
  'I',
  'LI',
  'OL',
  'P',
  'SPAN',
  'STRONG',
  'UL',
];

// A creator who writes a `div` or a `span` can give it an `id` for an anchor and
// a `class` for their own styles. `style` is never allowed.
const ALLOWED_ATTRIBUTES: Record<string, ReadonlyArray<string>> = {
  '*': LANGUAGE_MARK_ATTRIBUTES,
  A: ['href', 'title', 'target'],
  DIV: ['id', 'class'],
  SPAN: ['id', 'class'],
};

/**
 * What the HTML can have.
 *
 * - `showNotes` is the default. It is what the server allows in show notes, and
 *   so it is what the feed has. The podcast apps show only three levels of
 *   heading, so the server changes `h4` to `h6` into `h3`, and this does too. The
 *   preview of show notes, and every text that the server renders with the same
 *   sanitizer, use it.
 * - `web` is for text that only a Pinecast page shows, such as a page of a
 *   podcast website. It has no limit of levels of heading, and it can have
 *   preformatted text.
 *
 * No profile shows an image. Pinecast does not host images, and it does not want
 * to encourage a link to an image on another site. An image leaves its text
 * alternative.
 */
export type MarkdownProfile = 'showNotes' | 'web';

const PROFILES = {
  showNotes: {
    allowedTags: [...SHOW_NOTES_TAGS, 'H4', 'H5', 'H6'],
    renameTags: {H4: 'H3', H5: 'H3', H6: 'H3'},
  },
  web: {
    allowedTags: [...SHOW_NOTES_TAGS, 'H4', 'H5', 'H6', 'PRE'],
    renameTags: {},
  },
};

type Options = {
  profile?: MarkdownProfile;
};

// The members of a node of the tree of HTML that this file reads and writes.
type TreeNode = {
  type: string;
  tagName?: string;
  properties?: {alt?: unknown};
  value?: string;
  children?: Array<TreeNode>;
};

/**
 * Replaces each `img` in the tree with its text alternative, in place. An image
 * that has no text alternative leaves nothing.
 */
function replaceImagesWithAlt(node: TreeNode): void {
  if (!node.children) {
    return;
  }
  const children: Array<TreeNode> = [];
  for (const child of node.children) {
    if (child.type === 'element' && child.tagName === 'img') {
      const alt = child.properties?.alt;
      if (typeof alt === 'string' && alt) {
        children.push({type: 'text', value: alt});
      }
      continue;
    }
    replaceImagesWithAlt(child);
    children.push(child);
  }
  node.children = children;
}

export function markdownToHTML(
  markdown: string,
  {profile = 'showNotes'}: Options = {},
): string {
  // A tag between backticks is text, as it is on the server.
  const mdast = fromMarkdown(escapeTagsInCodeSpans(markdown), {
    extensions: [showNotesDialect],
  });
  const hast = toHast(mdast, {allowDangerousHtml: true});
  replaceImagesWithAlt(hast as TreeNode);
  const rawHTML = toHtml(hast, {allowDangerousHtml: true});

  return sanitizeHTML(rawHTML, {
    allowedAttributes: ALLOWED_ATTRIBUTES,
    ...PROFILES[profile],
  });
}
